import { FeeProfileType } from "@prisma/client";

export type Identity = {
  id: string;
  tags: { [key: string]: string };
};

export enum OPERATION_KEY {
  CARD_PRESENT_DEBIT = "CARD_PRESENT_DEBIT",
  CARD_PRESENT_UNREFERENCED_REFUND = "CARD_PRESENT_UNREFERENCED_REFUND",
}

export type Instrument = {
  id: string;
  currency: string;
  bin: string;
  brand: string;
  last_four: string;
  expiration_month: number;
  expiration_year: number;
  instrument_type: string; // PAYMENT_CARD || BANK_ACCOUNT
  masked_account_number: string;
  account_type: string;
  bank_account_validation_check: string;
  bank_code: string;
  country: string;
  card_type: string;
};

export type Transfer = {
  id: string;
  amount: number;
  failure_code: string;
  failure_message: string;
  fee: number;
  state: string;
  card_present_details: {
    masked_account_number: string;
    brand: string;
    payment_type: string;
    emv_data: {
      application_label: string;
    };
  };
  device: string;
  type: string;
  created_at: string;
  ready_to_settle_at: string | null;
  parent_transfer: string | null;
  tags: { [key: string]: string };
  subtype: string;
  merchant: string;
};

export type Settlement = {
  created_at: string;
  updated_at: string;
  merchant_id: string;
  net_amount: number;
  status: string;
  total_amount: number;
  total_fee: number;
  id: string;
};

export type SettlementEntrie = {
  id: string;
  created_at: string;
  amount: number;
  entity_id: string;
  entity_type: string;
  subtype: string;
  parent_entity_id: string;
  parent_entity_type: string;
  ready_to_settle_at: string;
};

export type IdentitySearchResponse = {
  _embedded: {
    identities: Array<Identity>;
  };
};

export type IdentitySearchParam = {
  firstName: string;
  lastName: string;
  officeId: number;
  patientId: number;
};

export type IdentityCreateParam = {
  firstName?: string | null;
  lastName?: string | null;
  birthday?: string | null;
  email?: string | null;
  phone?: string | null;
  officeId: number;
  patientId: number;
};

export type InstrumentCreateParam = {
  token: string;
  identity: string;
};

export type TransferCreateParam = {
  merchant: string | null;
  amount: number;
  idempotencyId?: string;
  source?: string;
  sessionId?: string;
  device?: string;
  operationKey?: OPERATION_KEY;
  tags?: any;
  surChargeAmount?: number;
};

export type RefundCreateParam = {
  amount: number;
  idempotencyId: string;
  transferId: string;
};

export type ListSettlementTransferParam = {
  settlement_id: string;
};

export type ListSettlementTransferResponse = {
  _embedded: {
    transfers: Transfer[];
  };
};

export type ListSettlementEntriesResponse = {
  _embedded: {
    settlement_entries: SettlementEntrie[];
  };
  _links: {
    self: {
      href: string;
    };
    next: {
      href: string;
    };
  };
  page: {
    limit: number;
    next_cursor: string | null;
  };
};

export type ListSettlementsParam = {
  merchant_id?: string;
  updated_at_gte?: string;
  after_cursor?: string;
  status?: string;
  page?: number;
  size?: number;
  application_id?: string;
  created_at_gte?: string;
  created_at_lte?: string;
  transfer_id?: string;
};

export type SettlementResponse = {
  _embedded: {
    settlements: Settlement[];
  };
  _links: {
    self: {
      href: string;
    };
    next: {
      href: string;
    };
  };
  page: {
    limit: number;
    next_cursor: string | null;
  };
};

export type WebHookResponse = {
  id: string;
  type: string;
  entity: string;
  occurred_at: string;
  _embedded: {
    [key: string]: { [key: string]: string | number | null | object }[];
  };
};

const baseUrl = process.env.FINIX_API_BASE_URL;
const userName = process.env.FINIX_API_USER_NAME;
const password = process.env.FINIX_API_PASSWORD;

export const token = btoa(`${userName}:${password}`);

export type FinixMerchantFeeProfileType = {
  cnpPercFee: number;
  cpPercFee: number;
  creditCardPercFee: number;
  debitCardPercFee: number;
  achPercFee: number;
  achPercFeeCap: number;
  feeProfileType: FeeProfileType;
};

export const defaultFinixMerchantFeeProfile: FinixMerchantFeeProfileType = {
  cnpPercFee: 0.0289,
  cpPercFee: 0.0239,
  creditCardPercFee: 0.0289,
  debitCardPercFee: 0.0219,
  achPercFee: 0.008,
  achPercFeeCap: 500, //$5
  feeProfileType: FeeProfileType.PAYMENT_TYPE,
};

type CalculateFeeProps = {
  amount: number;
  isTerminal?: boolean;
  isACHPayment: boolean;
  isCreditCardPayment?: boolean;
  applySurcharge: boolean;
  finixFeeProfile?: FinixMerchantFeeProfileType;
};

export const calculateFee = ({
  amount,
  isTerminal = false,
  isACHPayment,
  applySurcharge,
  isCreditCardPayment,
  finixFeeProfile = defaultFinixMerchantFeeProfile,
}: CalculateFeeProps) => {
  let fee = 0;
  if (isACHPayment) {
    const temp = amount * finixFeeProfile.achPercFee;
    fee = temp > finixFeeProfile.achPercFeeCap ? finixFeeProfile.achPercFeeCap : temp;
  } else if (finixFeeProfile.feeProfileType === FeeProfileType.PAYMENT_TYPE) {
    if (isTerminal) {
      fee =
        amount *
        (applySurcharge
          ? finixFeeProfile.cpPercFee / (1 - finixFeeProfile.cpPercFee)
          : finixFeeProfile.cpPercFee);
    } else {
      fee =
        amount *
        (applySurcharge
          ? finixFeeProfile.cnpPercFee / (1 - finixFeeProfile.cnpPercFee)
          : finixFeeProfile.cnpPercFee);
    }
  } else if (finixFeeProfile.feeProfileType === FeeProfileType.CARD_TYPE) {
    if (isCreditCardPayment) {
      fee =
        amount *
        (applySurcharge
          ? finixFeeProfile.creditCardPercFee / (1 - finixFeeProfile.creditCardPercFee)
          : finixFeeProfile.creditCardPercFee);
    } else {
      fee = amount * finixFeeProfile.debitCardPercFee;
    }
  }
  return Math.round(fee);
};

export const getIdentity = async (param: IdentitySearchParam): Promise<Identity[]> => {
  const { firstName, lastName, officeId, patientId } = param;
  const url = `identities?first_name=${firstName}&last_name=${lastName}&tags.key=patientId&tags.value=${patientId}`;

  try {
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "GET",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
    });

    if (response.ok) {
      const result: IdentitySearchResponse = await response.json();

      if (result._embedded) {
        const identities = result._embedded.identities;
        return identities.filter((id) => id.tags["officeId"] == officeId.toString());
      }

      return [];
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Get Identity Error" });
};

export const createIdentity = async (param: IdentityCreateParam): Promise<Identity> => {
  const { firstName, lastName, email, phone, officeId, patientId } = param;

  try {
    const response = await fetch(`${baseUrl}/identities`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
      body: JSON.stringify({
        entity: {
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone: phone,
        },
        tags: {
          patientId: patientId,
          officeId: officeId,
        },
      }),
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Create Identity Error" });
};

export type FinixIdentityPatient = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  cellPhone?: string | null;
  homePhone?: string | null;
  patientId?: number | null;
};

/** Find an existing Finix identity for the patient/office, or create one. */
export const getOrCreateFinixIdentity = async (
  patient: FinixIdentityPatient | null | undefined,
  officeId: number
): Promise<string> => {
  const firstName = patient?.firstName || "";
  const lastName = patient?.lastName || "";
  const patientId = patient?.patientId ?? -99999999;

  const buyers = await getIdentity({
    firstName,
    lastName,
    patientId,
    officeId,
  });

  if (buyers.length) {
    return buyers[0].id;
  }

  const identity = await createIdentity({
    firstName: patient?.firstName || "",
    lastName: patient?.lastName || "",
    email: patient?.email || "",
    phone: patient?.phoneNumber || patient?.cellPhone || patient?.homePhone || "",
    patientId,
    officeId,
  });

  return identity?.id || "";
};

export const createPaymentInstrument = async (param: InstrumentCreateParam): Promise<Instrument> => {
  const { token: paymentToken, identity } = param;

  try {
    const response = await fetch(`${baseUrl}/payment_instruments`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
      body: JSON.stringify({
        token: paymentToken,
        type: "TOKEN",
        identity: identity,
      }),
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Create Instrument Error" });
};

export const createTransfer = async (param: TransferCreateParam): Promise<Transfer> => {
  const { amount, idempotencyId, merchant, source, sessionId, device, operationKey, tags, surChargeAmount } =
    param;
  try {
    const response = await fetch(`${baseUrl}/transfers`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
      body: JSON.stringify({
        amount: amount,
        currency: "USD",
        merchant,
        source: source || null,
        fraud_session_id: sessionId || null,
        idempotency_id: idempotencyId || null,
        device: device || null,
        operation_key: operationKey || null,
        tags: {
          "App Name": "QuantaPay",
          ...(tags ? tags : {}),
        },
        additional_buyer_charges: surChargeAmount
          ? {
              surcharge_amount: surChargeAmount,
            }
          : null,
      }),
    });

    if (response.ok) {
      return await response.json();
    } else {
      const result = await response.json();
      console.log("TRANSACTION ERROR: ", merchant, source, " - ", result._embedded.errors[0].message);
      return Promise.reject({ failure_message: result._embedded.errors[0].message });
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
    return Promise.reject({ failure_message: "Some error" });
  }
};

export const createRefund = async (param: RefundCreateParam): Promise<Transfer> => {
  const { amount, idempotencyId, transferId } = param;

  try {
    const response = await fetch(`${baseUrl}/transfers/${transferId}/reversals`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
      body: JSON.stringify({
        refund_amount: amount,
        currency: "USD",
        idempotency_id: idempotencyId,
      }),
    });

    if (response.ok) {
      return await response.json();
    } else {
      const result = await response.json();
      console.log("TRANSACTION ERROR: ", transferId, " - ", result._embedded.errors[0].message);
      return Promise.reject({ failure_message: result._embedded.errors[0].message });
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
    return Promise.reject({ failure_message: "Some error" });
  }
};

export const listSettlements = async (param: ListSettlementsParam): Promise<Settlement[]> => {
  const { merchant_id, created_at_gte, created_at_lte, status, transfer_id, updated_at_gte } = param;

  let url = `${baseUrl}/settlements?sort=created_at,desc&sort=id,desc`;

  if (merchant_id) {
    url += `&merchant_id=${merchant_id}`;
  }
  if (updated_at_gte) {
    url += `&updated_at.gte=${updated_at_gte}`;
  }
  if (created_at_gte && created_at_lte) {
    url += `&created_at.gte=${created_at_gte}&created_at.lte=${created_at_lte}`;
  }
  if (status) {
    url += `&status=${status}`;
  }
  if (transfer_id) {
    url += `&transfer_id=${transfer_id}`;
  }

  const settlements: Settlement[] = [];

  try {
    do {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
          "Finix-Version": "2022-02-01",
        },
      });

      if (response.ok) {
        const result: SettlementResponse = await response.json();
        const settle = result._embedded?.settlements || [];
        settlements.push(...settle);
        if (settle.length < result.page.limit) {
          break;
        }
        url = result._links.next.href;
      } else {
        throw new Error("Error");
      }
    } while (true);
    return settlements;
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }
  return Promise.reject({ message: "Error" });
};

export const listSettlementTransfers = async (
  param: ListSettlementTransferParam
): Promise<ListSettlementTransferResponse> => {
  const { settlement_id } = param;

  const url = `${baseUrl}/settlements/${settlement_id}/transfers`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Error" });
};

export const listSettlementEntries = async (
  param: ListSettlementTransferParam
): Promise<SettlementEntrie[]> => {
  const { settlement_id } = param;

  let url = `${baseUrl}/settlements/${settlement_id}/entries`;

  const entries: SettlementEntrie[] = [];

  try {
    do {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Basic ${token}`,
          "Content-Type": "application/json",
          "Finix-Version": "2022-02-01",
        },
      });

      if (response.ok) {
        const result: ListSettlementEntriesResponse = await response.json();
        const subentries = result._embedded.settlement_entries;
        entries.push(...subentries);
        if (subentries.length < result.page.limit) {
          break;
        }
        url = result._links.next.href;
      } else {
        throw new Error("Error");
      }
    } while (true);

    return entries;
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Error" });
};

export const getSettlement = async (param: { settlement_id: string }): Promise<Settlement> => {
  const { settlement_id } = param;

  const url = `${baseUrl}/settlements/${settlement_id}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Error" });
};

type PayoutProfile = {
  id: string;
  created_at: string;
  updated_at: string;
  linked_id: string;
  linked_type: string;
  gross: {
    payouts: {
      frequency: string;
      submission_delay_days: number;
      payment_instrument_id: string;
      rail: string;
    };
    fees: {
      frequency: string;
      day_of_month: number;
      submission_delay_days: number;
      payment_instrument_id: string;
      rail: string;
    };
  };
  net: {
    frequency: string;
    submission_delay_days: number;
    payment_instrument_id: string;
    rail: string;
  };
  tags: any;
  type: string;
};

export const getPayoutProfileByMerchant = async (param: { merchant_id: string }): Promise<PayoutProfile> => {
  const { merchant_id } = param;

  const url = `${baseUrl}/merchants/${merchant_id}/payout_profile`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Error" });
};

export type PaymentInstrument = {
  id: string;
  created_at: string;
  updated_at: string;
  application: string;
  created_via: string;
  currency: string;
  disabled_code: string;
  disabled_message: string;
  enabled: boolean;
  fingerprint: string;
  identity: string;
  instrument_type: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  address_verification: string;
  bin: string;
  brand: string;
  card_type: string;
  expiration_month: number;
  expiration_year: number;
  issuer_country: string;
  last_four: string;
  name: string;
  security_code_verification: string;
  tags: any;
  type: string;
  account_type: string;
  bank_code: string; // routing number
  masked_account_number: string; // account number
};

export const getPaymentInstrumentById = async (param: {
  instrument_id: string;
}): Promise<PaymentInstrument> => {
  const { instrument_id } = param;

  const url = `${baseUrl}/payment_instruments/${instrument_id}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
        "Finix-Version": "2022-02-01",
      },
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e: any) {
    console.log("ERROR", e?.message);
  }

  return Promise.reject({ message: "Error" });
};
